import type { SessionContext, StartResponse } from '../types/chat.js';

export type IdentitySnapshot = {
  email?: string;
  firstName?: string;
  otpVerified: boolean;
};

export type ResendOTPResult = {
  sent?: boolean;
  alreadyVerified?: boolean;
  rateLimited?: boolean;
  retryAfterSeconds?: number;
  email?: string;
  expiresInSeconds?: number;
  error?: string;
};

// A deploy restarts the server and leaves a few seconds with no upstream:
// nginx answers 502 without CORS headers, which the browser reports as a
// TypeError ("Failed to fetch"). The request never reached the bot, so it is
// safe to send it again. Retried only BEFORE a response exists: a stream that
// breaks halfway is never replayed (the turn may already have run).
const RETRY_DELAYS_MS = [1500, 3000, 5000, 8000];

function retryable(res: Response): boolean {
  return res.status === 502 || res.status === 503 || res.status === 504;
}

async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(url, init);
      if (!retryable(res) || attempt >= RETRY_DELAYS_MS.length) return res;
    } catch (e) {
      if ((e as Error).name === 'AbortError' || attempt >= RETRY_DELAYS_MS.length) throw e;
    }
    await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    if (init.signal?.aborted) throw new DOMException('aborted', 'AbortError');
  }
}

export class ChatAPI {
  constructor(private readonly baseURL: string) {}

  /**
   * Ownership secret for the current conversation, held in memory only.
   * Sent on every conversation-scoped call; the server refuses without it
   * (the conversation id alone travels into Desk tickets and server logs, so
   * it is only a reference).
   */
  private secret = '';

  private owned(extra: Record<string, string> = {}): Record<string, string> {
    return this.secret ? { ...extra, 'X-Conversation-Secret': this.secret } : extra;
  }

  async start(ctx: SessionContext): Promise<StartResponse> {
    const res = await fetchWithRetry(`${this.baseURL}/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ctx),
    });
    if (!res.ok) {
      throw new Error(`start failed: ${res.status}`);
    }
    const started: StartResponse = await res.json();
    this.secret = started.secret ?? '';
    return started;
  }

  async streamMessage(
    conversationId: string,
    text: string,
    signal: AbortSignal,
  ): Promise<Response> {
    const res = await fetchWithRetry(`${this.baseURL}/messages`, {
      method: 'POST',
      headers: this.owned({ 'Content-Type': 'application/json', Accept: 'text/event-stream' }),
      body: JSON.stringify({ conversationId, text }),
      signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`message failed: ${res.status}`);
    }
    return res;
  }

  async identity(conversationId: string): Promise<IdentitySnapshot> {
    const res = await fetch(`${this.baseURL}/conversations/${encodeURIComponent(conversationId)}/identity`, {
      headers: this.owned(),
    });
    if (!res.ok) throw new Error(`identity failed: ${res.status}`);
    return res.json();
  }

  async logout(conversationId: string): Promise<void> {
    const res = await fetch(`${this.baseURL}/conversations/${encodeURIComponent(conversationId)}/logout`, {
      method: 'POST',
      headers: this.owned(),
    });
    if (!res.ok) throw new Error(`logout failed: ${res.status}`);
  }

  /**
   * Records the GDPR consent decision. Server-authoritative: the backend
   * writes the audit row (truncated IP, policy hashes, timestamp) from
   * HTTP-layer facts. Must complete BEFORE the follow-up chat message is
   * sent, so the gate is open when the model resumes.
   */
  async consent(conversationId: string, decision: 'accepted' | 'declined'): Promise<void> {
    const res = await fetch(`${this.baseURL}/conversations/${encodeURIComponent(conversationId)}/consent`, {
      method: 'POST',
      headers: this.owned({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ decision }),
    });
    if (!res.ok) throw new Error(`consent failed: ${res.status}`);
  }

  async resendOTP(conversationId: string): Promise<ResendOTPResult> {
    const res = await fetch(`${this.baseURL}/conversations/${encodeURIComponent(conversationId)}/otp/resend`, {
      method: 'POST',
      headers: this.owned(),
    });
    return res.json();
  }
}
